import { useRef } from "react"

import { Stack, Tab, Tabs, Typography } from "@mui/material"
import { useQueryAdminDashboardKpi } from "api/reactQuery/queries/adminDashboard"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { IDashboardValidationError } from "ts/interfaces/AdminDashboard"
import { DashboardTab } from "ts/types/AdminDashboard"
import dayjs from "utils/dayjs"

import { dashboardTitleSx } from "./AdminDashboard.styles"
import AllStudents, { IAllStudentsHandle } from "./AllStudents"
import AllTeachers from "./AllTeachers"
import BackToTop from "./BackToTop"
import BonusesVsFines from "./BonusesVsFines"
import ClassSummary from "./ClassSummary"
import FilterBar from "./FilterBar"
import KpiSummary from "./KpiSummary"
import StoreItems from "./StoreItems"
import StudentsAttention from "./StudentsAttention"
import StudentsKpiSummary from "./StudentsKpiSummary"
import TeachersKpiSummary from "./TeachersKpiSummary"
import useDashboardPeriod from "./useDashboardPeriod"

const ENABLED_TABS: DashboardTab[] = ["overview", "teachers", "students"]

const TITLE_KEYS: Record<DashboardTab, string> = {
  overview: "adminDashboard.adminDashboard",
  teachers: "adminDashboard.teacherUsage",
  students: "adminDashboard.studentUsage",
}

// The FE validates custom ranges, but BED also refuses dates older than the data
// it keeps (400 out_of_retention); its message is shown as a fallback.
const getOutOfRetentionMessage = (error: AxiosError | null) => {
  const data = error?.response?.data as
    | Partial<Record<"detail", unknown>>
    | undefined
  if (!Array.isArray(data?.detail)) return undefined
  return (data.detail as IDashboardValidationError["detail"]).find(
    (item) => item?.type === "value_error.out_of_retention",
  )?.msg
}

const AdminDashboard = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get("tab")
  const tab = ENABLED_TABS.find((enabled) => enabled === rawTab) ?? "overview"
  const { params } = useDashboardPeriod(tab)
  const kpi = useQueryAdminDashboardKpi(params)
  const asOf = kpi.data?.data.as_of
  const outOfRetentionMessage = getOutOfRetentionMessage(kpi.error)
  const allStudentsRef = useRef<IAllStudentsHandle>(null)

  const selectTab = (next: DashboardTab) => {
    const merged = new URLSearchParams(searchParams)
    if (next === "overview") merged.delete("tab")
    else merged.set("tab", next)
    setSearchParams(merged)
  }

  const withCount = (label: string, count: number | undefined) =>
    count === undefined ? label : `${label} (${count.toLocaleString("en-US")})`

  return (
    <Stack
      rowGap={4}
      sx={{
        width: "100%",
        maxWidth: 960,
        mx: "auto",
        px: { mobile: 2, tablet: 0 },
        pb: 10,
      }}
    >
      <Stack rowGap={1}>
        <Typography variant="h2" component="h1" color="mockup.neutral10">
          {t(TITLE_KEYS[tab])}
        </Typography>
        <Typography variant="body1" color="mockup.neutral30" fontWeight={500}>
          {asOf
            ? t("adminDashboard.lastUpdated", {
                date: dayjs(asOf).format("MMM DD, YYYY, h:mm A"),
              })
            : " "}
        </Typography>
        {tab !== "overview" && (
          <Typography
            variant="body2"
            color="mockup.neutral30"
            fontWeight={500}
            maxWidth={720}
          >
            {t(`adminDashboard.description_${tab}`)}
          </Typography>
        )}
      </Stack>
      <Tabs
        value={tab}
        onChange={(_, next: DashboardTab) => selectTab(next)}
        sx={{
          boxShadow: (theme) =>
            `inset 0 -1px 0 ${theme.palette.mockup.neutral90}`,
          "& .MuiTabs-indicator": { backgroundColor: "mockup.primary60" },
          "& .MuiTab-root": {
            fontSize: "18px",
            fontWeight: 500,
            color: "mockup.neutral30",
          },
          "& .MuiTab-root.Mui-selected": {
            fontWeight: 700,
            color: "mockup.primary60",
          },
        }}
      >
        <Tab value="overview" label={t("adminDashboard.overview")} />
        <Tab
          value="teachers"
          label={withCount(
            t("adminDashboard.teachers"),
            kpi.data?.data.active_teachers,
          )}
        />
        <Tab
          value="students"
          label={withCount(
            t("adminDashboard.students"),
            kpi.data?.data.active_students,
          )}
        />
      </Tabs>
      <FilterBar tab={tab} />
      {outOfRetentionMessage && (
        <Typography variant="body2" color="error" role="alert">
          {outOfRetentionMessage}
        </Typography>
      )}
      {tab === "overview" && (
        <>
          <Stack rowGap={3}>
            <Typography
              variant="body1"
              component="h2"
              color="mockup.neutral0"
              sx={dashboardTitleSx}
            >
              {t("adminDashboard.overview")}
            </Typography>
            <KpiSummary query={kpi} />
          </Stack>
          <ClassSummary params={params} />
          <BonusesVsFines params={params} />
          <StoreItems params={params} />
        </>
      )}
      {tab === "teachers" && (
        <>
          <TeachersKpiSummary params={params} />
          <AllTeachers params={params} />
        </>
      )}
      {tab === "students" && (
        <>
          <StudentsKpiSummary params={params} />
          <StudentsAttention
            params={params}
            onApply={(view) => allStudentsRef.current?.applyView(view)}
          />
          <AllStudents ref={allStudentsRef} params={params} />
        </>
      )}
      <BackToTop />
    </Stack>
  )
}

export default AdminDashboard
